import React, { useEffect, useState } from 'react';
import DOMPurify from 'dompurify';
import styles from './PrequalificationStep.module.css';
import { getInsurances, type Insurance } from '../../utils/apis';
import AxisRejectionPopup, { type RejectionResource } from './AxisRejectionPopup';

interface PrequalificationStepProps {
  onBack: () => void;
  onProceed: () => void;
  initialAnswers?: Record<number, string>;
  onAnswersChange?: (answers: Record<number, string>) => void;
  onInsuranceChange?: (insurance: string) => void;
}

type QuestionType = 'yesno' | 'info' | 'insurance';

type QuestionId =
  | 'age18'
  | 'caregiver'
  | 'careType'
  | 'longActing'
  | 'schizophrenia'
  | 'detoxRehab'
  | 'substanceUse'
  | 'thirdParty'
  | 'insurance'
  | 'smsConsent';

interface PrequalificationQuestion {
  // Stable identifier — decouples cross-question logic from array order
  id: QuestionId;
  text: string;
  type: QuestionType;
  // Answer that ALLOWS proceeding (yes/no questions only)
  allowedAnswer?: 'Yes' | 'No';
  // Options for informational single-select questions
  options?: string[];
  // Only render this question when the referenced question has this answer
  showWhen?: { questionId: QuestionId; equals: string };
}

// The rejection copy shown when a patient's answers indicate they are not a good fit.
const REJECTION_INTRO =
  'Thanks for your interest in Axis Integrated Mental Health! Based on your needs for your care, we recommend specialized programs for mental illness or other community resources that may be a better fit given your needs. We encourage you to contact the resources below:';

const REJECTION_RESOURCES: RejectionResource[] = [
  { name: 'WellMinded Counseling', tag: 'Therapy resource' },
  { name: 'Mirador Counseling and Consulting', tag: 'Therapy resource' },
  { name: 'Lifestance' },
  { name: 'Lee & Me Psychiatry' },
  { name: 'Mindful Minds Psychiatry' },
  { name: 'Brain and Body Integration' },
  { name: 'CuraWest' },
];

// Disqualifying insurance keyed off the admin-managed display name. Matched
// exactly (trimmed, case-insensitive) on purpose: a substring match would
// false-positive on any future plan whose name contains this text. If the
// insurances API later exposes a stable id/flag for this, prefer that.
const DISQUALIFYING_INSURANCE = 'Kaiser Permanente';

const isDisqualifyingInsurance = (insurance: string | undefined): boolean =>
  !!insurance && insurance.trim().toLowerCase() === DISQUALIFYING_INSURANCE.toLowerCase();

const QUESTIONS: PrequalificationQuestion[] = [
  { id: 'age18', text: 'Are you 18 years or older?', type: 'yesno', allowedAnswer: 'Yes' },
  {
    id: 'caregiver',
    text: 'Are you a parent/caregiver for someone age 15-17?',
    type: 'yesno',
    allowedAnswer: 'Yes',
    showWhen: { questionId: 'age18', equals: 'No' },
  },
  {
    id: 'careType',
    text: 'What type of care are you seeking?',
    type: 'info',
    options: ['Medication only', 'Medication + therapy', 'Medication + Spravato', 'Medication + TMS', 'Unsure'],
  },
  { id: 'longActing', text: 'Are you seeking long acting injectables or ketamine infusions/IM treatment?', type: 'yesno', allowedAnswer: 'No' },
  { id: 'schizophrenia', text: 'Have you ever been diagnosed with schizophrenia or schizoaffective disorder?', type: 'yesno', allowedAnswer: 'No' },
  {
    id: 'detoxRehab',
    text: 'Are you seeking immediate detox from substance use or residential/inpatient rehabilitation services?',
    type: 'yesno',
    allowedAnswer: 'No',
  },
  { id: 'substanceUse', text: 'Are you seeking suboxone, methadone, or buprenorphine?', type: 'yesno', allowedAnswer: 'No' },
  {
    id: 'thirdParty',
    text: 'Are you seeking a one-time evaluation or assessment for a third party requirement (e.g. legal, employment, disability, transplant, or EAP)?',
    type: 'yesno',
    allowedAnswer: 'No',
  },
  { id: 'insurance', text: 'What kind of insurance do you have?', type: 'insurance' },
  {
    id: 'smsConsent',
    text: 'I consent to receive SMS notifications and alerts from Axis Integrated Mental Health. Message frequency varies. Message & data rates may apply. You can Text HELP for assistance or Reply STOP to unsubscribe at any time.',
    type: 'yesno',
    allowedAnswer: 'Yes',
  },
];

// Positions derived from stable ids so cross-question logic survives reordering
const indexOfId = (id: QuestionId): number => QUESTIONS.findIndex((q) => q.id === id);
const Q_AGE_18 = indexOfId('age18');
const Q_CAREGIVER = indexOfId('caregiver');
const Q_INSURANCE = indexOfId('insurance');

const PrequalificationStep: React.FC<PrequalificationStepProps> = ({
  onBack,
  onProceed,
  initialAnswers = {},
  onAnswersChange,
  onInsuranceChange,
}) => {
  const [answers, setAnswers] = useState<Record<number, string>>(initialAnswers);
  const [showRejection, setShowRejection] = useState(false);

  const [insurances, setInsurances] = useState<Insurance[]>([]);
  const [insurancesLoading, setInsurancesLoading] = useState(true);
  const [insurancesError, setInsurancesError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getInsurances(true)
      .then((data) => {
        if (!cancelled) setInsurances(data);
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('Failed to load insurances', err);
          setInsurancesError('Unable to load insurance options');
        }
      })
      .finally(() => {
        if (!cancelled) setInsurancesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Whether a question should be rendered given the current answers
  const isVisible = (question: PrequalificationQuestion): boolean => {
    if (!question.showWhen) return true;
    return answers[indexOfId(question.showWhen.questionId)] === question.showWhen.equals;
  };

  const handleChange = (questionIndex: number, value: string) => {
    const newAnswers = { ...answers, [questionIndex]: value };

    // Clear the caregiver answer if the patient is no longer under the "not 18" branch
    if (questionIndex === Q_AGE_18 && value !== 'No') {
      delete newAnswers[Q_CAREGIVER];
    }

    setAnswers(newAnswers);
    onAnswersChange?.(newAnswers);

    if (questionIndex === Q_INSURANCE) {
      onInsuranceChange?.(value);
    }
  };

  // Insurance options can't be loaded (API failed or returned nothing). We don't
  // want to trap the patient on a public intake page, so the insurance question
  // stops being a hard gate — insurance is collected again on the next step.
  const insuranceUnavailable = !insurancesLoading && (insurancesError !== null || insurances.length === 0);

  // Every visible question must be answered before we can evaluate/proceed.
  // The insurance question is exempt when its options can't be loaded.
  const allAnswered = QUESTIONS.every((q, index) => {
    if (!isVisible(q)) return true;
    if (q.type === 'insurance' && insuranceUnavailable) return true;
    return (answers[index] ?? '') !== '';
  });

  // Returns true when the answers indicate the patient is NOT a good fit
  const isDisqualified = (): boolean => {
    // Age is handled as a pair below, not by the generic per-question check: a patient
    // under 18 still qualifies if they are a parent/caregiver for someone age 15-17.
    // Disqualify only when they are not 18+ AND not such a caregiver.
    if (answers[Q_AGE_18] === 'No' && answers[Q_CAREGIVER] === 'No') return true;

    for (let i = 0; i < QUESTIONS.length; i++) {
      // Skip the age/caregiver pair — evaluated together above.
      if (i === Q_AGE_18 || i === Q_CAREGIVER) continue;
      const q = QUESTIONS[i];
      if (!isVisible(q)) continue;
      if (q.type === 'yesno' && q.allowedAnswer && answers[i] !== q.allowedAnswer) return true;
      if (q.type === 'insurance' && isDisqualifyingInsurance(answers[i])) return true;
    }
    return false;
  };

  const handleProceed = () => {
    if (isDisqualified()) {
      setShowRejection(true);
      return;
    }
    onProceed();
  };

  return (
    <div className={styles.preQualificationAssessment}>
      {/* Header/Navigation */}
      <div className={styles.navBar}>
        <span className={styles.navPhone}>(720) 400-7025</span>
        <div className={styles.navLinks}>
          <button className={styles.navLink} onClick={() => window.location.reload()}>HOME</button>
        </div>
      </div>

      {/* Main Content */}
      <div className={styles.mainContent}>
        <h1 className={styles.title}>Pre-qualification Assessment</h1>
        <p className={styles.subtitle}>
          Please answer the following questions honestly to ensure we can provide you with the appropriate care.
        </p>

        <div className={styles.card}>
          <div className={styles.sectionTitle}>Health and Safety Screening</div>
          <div className={styles.sectionSubtitle}>
            This brief assessment helps us determine if online booking is appropriate for your current needs.
          </div>

          <div className={styles.questionsList}>
            {QUESTIONS.map((question, index) => {
              if (!isVisible(question)) return null;
              return (
                <div key={index} className={styles.questionItem}>
                  <div className={styles.questionText}>
                    <span className={styles.questionNumber}>{index + 1}.</span>
                    <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(question.text) }} />
                  </div>

                  {question.type === 'yesno' && (
                    <div className={styles.radioGroup}>
                      <label className={styles.radioOption}>
                        <input
                          type="radio"
                          name={`question-${index}`}
                          value="Yes"
                          checked={answers[index] === 'Yes'}
                          onChange={() => handleChange(index, 'Yes')}
                          className={styles.radioInput}
                        />
                        <span className={styles.radioLabel}>Yes</span>
                      </label>
                      <label className={styles.radioOption}>
                        <input
                          type="radio"
                          name={`question-${index}`}
                          value="No"
                          checked={answers[index] === 'No'}
                          onChange={() => handleChange(index, 'No')}
                          className={styles.radioInput}
                        />
                        <span className={styles.radioLabel}>No</span>
                      </label>
                    </div>
                  )}

                  {question.type === 'info' && (
                    <select
                      className={styles.formSelect}
                      value={answers[index] ?? ''}
                      onChange={(e) => handleChange(index, e.target.value)}
                    >
                      <option value="">Select an option</option>
                      {question.options?.map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  )}

                  {question.type === 'insurance' && (
                    <>
                      <select
                        className={styles.formSelect}
                        value={answers[index] ?? ''}
                        onChange={(e) => handleChange(index, e.target.value)}
                        disabled={insurancesLoading}
                      >
                        <option value="">{insurancesLoading ? 'Loading...' : 'Select Insurance'}</option>
                        {insurances.map((item) => (
                          <option key={item.name} value={item.name}>{item.name}</option>
                        ))}
                      </select>
                      {insurancesError && <div className={styles.formError}>{insurancesError}</div>}
                      {insuranceUnavailable && (
                        <div className={styles.helperText}>You can select your insurance on the next step.</div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>

          <button
            className={styles.proceedButton}
            onClick={handleProceed}
            disabled={!allAnswered}
          >
            Proceed to Patient Information
          </button>
        </div>

        <button className={styles.backButton} onClick={onBack}>
          ← Back
        </button>
      </div>

      {/* Footer */}
      <div className={styles.footer}>
        <div className={styles.footerTitle}>Axis Integrated Mental Health</div>
        <div className={styles.footerInfo}>
          <div className={styles.footerInfoItem}>
            <span>📞</span>
            <span>Phone (appointments): 720-400-7025</span>
          </div>
          <div className={styles.footerInfoItem}>
            <span>📍</span>
          </div>
        </div>
        <div className={styles.rating}>
          <span className={styles.star}>★</span>
          <span className={styles.star}>★</span>
          <span className={styles.star}>★</span>
          <span className={styles.star}>★</span>
          <span className={styles.star}>★</span>
        </div>
        <div className={styles.ratingText}>Average Rating: 4.9/5</div>
      </div>

      {/* Accessibility Icon */}
      <div className={styles.accessibilityIcon}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="4" r="2" fill="white"/>
          <path d="M12 6v8M8 14h8M12 18v4M8 22h8" stroke="white" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      </div>

      <AxisRejectionPopup
        isOpen={showRejection}
        intro={REJECTION_INTRO}
        resources={REJECTION_RESOURCES}
        onClose={() => setShowRejection(false)}
      />
    </div>
  );
};

export default PrequalificationStep;
